import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MIcon } from '../../lib/micon';
import { useIsMobile, useEindeutigeKlassen } from '../../lib/hooks';
import { useSchueler } from '../../contexts/SchuelerContext';
import { schuljahrFenster } from '../../lib/schuljahr';
import { nimmNotizenFilter } from '../../lib/nav';
import { zeigeApiFehler } from '../../lib/api';
import { toast } from '../../lib/toast';
import {
  Notiz, NOTIZEN_RELOAD, WAEHLBARE_KATEGORIEN, katInfo, notizText, ttmm, initialen, MONATE,
  ladeNotizen, setzePin, setzeArchiv, kopiereNotizen, loescheMitRueckgaengig, signalNotizenGeaendert,
} from './notizen-lib';
import { NotizSheet } from './NotizSheet';
import { heuteIso, wtKommaTm } from '../schueler/profil/format';
import { PortalMenu } from '../klasse/KlassenKopf';
import '../klasse/klasse.css';
import './Notizen.css';
import { LdAuswahl } from '../../components/ld/LdFenster';

const SEITE = 50;

// Welle K2: Gruppen nach Zeit — „Diese Woche“, „Letzte Woche“, danach je Monat
const plusTage = (iso: string, n: number) => { const x = new Date(iso + 'T12:00:00Z'); x.setUTCDate(x.getUTCDate() + n); return x.toISOString().slice(0, 10); };
const montagVon = (iso: string) => plusTage(iso, -((new Date(iso + 'T12:00:00Z').getUTCDay() + 6) % 7));
function zeitGruppe(datum: string, heute: string): string {
  const mo = montagVon(heute);
  const d = datum.slice(0, 10);
  if (d >= mo) return 'Diese Woche';
  if (d >= plusTage(mo, -7)) return 'Letzte Woche';
  return `${MONATE[Number(d.slice(5, 7)) - 1]} ${d.slice(0, 4)}`;
}

// Welle U4b: Notizen als eigener Bereich — Aktuell/Archiv, Suche, Filter (Klasse, Schüler/in,
// Schuljahr, Kategorie), Liste (Angepinnt, dann nach Zeit), Mehrfachauswahl, "Weitere laden".
// Welle U8: vorgabeKlasseId gesetzt = eingebettet im Klassen-Screen — Klassenfilter fest auf die
// Klasse oben, die eigene Klassenauswahl entfällt. Ohne (eigenständig) wie bisher.
// Welle K2: ruhig — ein Hauptknopf „+ Notiz“, Kategorie als kleiner Punkt mit Namen, keine Farbflächen.
export function NotizenBereich({ kopfZusatz, vorgabeKlasseId }: { kopfZusatz?: React.ReactNode; vorgabeKlasseId?: number | null }) {
  const eingebettet = vorgabeKlasseId !== undefined;
  const isMobile = useIsMobile();
  const klassen = useEindeutigeKlassen();
  const { schuelerListe } = useSchueler();
  const sjAktuell = schuljahrFenster().label;
  const sjStart = Number(sjAktuell.slice(0, 4));
  const sjOptionen = [
    { wert: 'aktuell', label: sjAktuell },
    { wert: `${sjStart - 1}/${String(sjStart).slice(2)}`, label: `${sjStart - 1}/${String(sjStart).slice(2)}` },
    { wert: `${sjStart - 2}/${String(sjStart - 1).slice(2)}`, label: `${sjStart - 2}/${String(sjStart - 1).slice(2)}` },
    { wert: '', label: 'Alle' },
  ];

  const [archiv, setArchiv] = useState(false);
  const [suche, setSuche] = useState('');
  const [sucheVerzoegert, setSucheVerzoegert] = useState('');
  const [klasseId, setKlasseId] = useState<number | ''>('');
  const [schuelerId, setSchuelerId] = useState<number | ''>('');
  // Welle U8: eingebettet Klassenfilter = Klasse oben (Schülerfilter bei Wechsel zurücksetzen, offenes Sheet schließen)
  useEffect(() => {
    if (!eingebettet) return;
    setKlasseId(vorgabeKlasseId ?? '');
    setSchuelerId('');
    setSheet(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eingebettet, vorgabeKlasseId]);
  const [schuljahr, setSchuljahr] = useState('aktuell');
  const [kategorie, setKategorie] = useState<string | null>(null);
  const [notizen, setNotizen] = useState<Notiz[] | null>(null);
  const [gesamt, setGesamt] = useState(0);
  const [kategorien, setKategorien] = useState<Record<string, number>>({});
  const [anzahl, setAnzahl] = useState<{ aktuell: number; archiv: number }>({ aktuell: 0, archiv: 0 });
  const [auswahlModus, setAuswahlModus] = useState(false);
  const [auswahl, setAuswahl] = useState<Set<number>>(new Set());
  const [sheet, setSheet] = useState<{ notiz: Notiz | null } | null>(null);
  const [menu, setMenu] = useState<{ id: number; el: HTMLElement } | null>(null);
  const [filterOffen, setFilterOffen] = useState(false);
  const ladeNr = useRef(0);

  // Sprung aus dem Profil ("Alle n Notizen von X"): Schülerfilter vorbelegen, alle Schuljahre
  // Welle U6b: aus der Suche zusätzlich genau diese Notiz im Sheet öffnen
  const zielNotizRef = useRef<number | null>(null);
  useEffect(() => {
    const f = nimmNotizenFilter();
    if (f) {
      setSchuelerId(f.schuelerId); setSchuljahr(''); if (f.klasseId) setKlasseId(f.klasseId);
      if (f.notizId) { zielNotizRef.current = f.notizId; if (f.archiv) setArchiv(true); }
    }
  }, []);
  useEffect(() => {
    if (zielNotizRef.current == null || !notizen) return;
    const n = notizen.find(x => x.id === zielNotizRef.current);
    if (n) { zielNotizRef.current = null; setSheet({ notiz: n }); }
  }, [notizen]);
  useEffect(() => { const t = setTimeout(() => setSucheVerzoegert(suche.trim()), 300); return () => clearTimeout(t); }, [suche]);

  const filter = useMemo(() => ({
    klasse_id: klasseId || undefined, schueler_id: schuelerId || undefined,
    schuljahr: schuljahr || undefined, q: sucheVerzoegert || undefined,
  }), [klasseId, schuelerId, schuljahr, sucheVerzoegert]);

  const lade = useCallback(async (anhaengen = false, aktuelleListe: Notiz[] = []) => {
    const nr = ++ladeNr.current;
    try {
      const [r, a, b] = await Promise.all([
        ladeNotizen({ ...filter, kategorie: kategorie ?? undefined, archiv: archiv ? 1 : 0, limit: SEITE, offset: anhaengen ? aktuelleListe.length : 0 }),
        ladeNotizen({ ...filter, archiv: 0, limit: 1 }),
        ladeNotizen({ ...filter, archiv: 1, limit: 1 }),
      ]);
      if (nr !== ladeNr.current) return;   // veraltete Antwort (schnelles Tippen) verwerfen
      setNotizen(anhaengen ? [...aktuelleListe, ...r.notizen] : r.notizen);
      setGesamt(r.gesamt); setKategorien(r.kategorien ?? {});
      setAnzahl({ aktuell: a.gesamt, archiv: b.gesamt });
    } catch (err) { zeigeApiFehler(err, 'Notizen konnten nicht geladen werden'); setNotizen([]); }
  }, [filter, kategorie, archiv]);

  useEffect(() => { lade(false); }, [lade]);
  useEffect(() => {
    const neu = () => lade(false);
    window.addEventListener(NOTIZEN_RELOAD, neu);
    return () => window.removeEventListener(NOTIZEN_RELOAD, neu);
  }, [lade]);
  useEffect(() => { setAuswahl(new Set()); }, [archiv, filter, kategorie]);

  const schuelerOptionen = useMemo(() => schuelerListe
    .filter(s => !klasseId || Number(s.klasse_id) === Number(klasseId))
    .sort((a, b) => a.name.localeCompare(b.name, 'de')), [schuelerListe, klasseId]);

  const liste = useMemo(() => notizen ?? [], [notizen]);
  // Angepinnte zuerst, dann nach Zeit (Diese Woche, Letzte Woche, je Monat)
  const gruppen = useMemo(() => {
    const heute = heuteIso();
    const m = new Map<string, Notiz[]>();
    const angepinnt = liste.filter(n => n.angepinnt);
    if (angepinnt.length) m.set('Angepinnt', angepinnt);
    for (const n of liste.filter(x => !x.angepinnt)) {
      const k = zeitGruppe(n.datum, heute);
      if (!m.has(k)) m.set(k, []);
      m.get(k)!.push(n);
    }
    return Array.from(m.entries());
  }, [liste]);
  const katListe = Array.from(new Set([...WAEHLBARE_KATEGORIEN, ...Object.keys(kategorien)]));

  function umschalten(n: Notiz) {
    setAuswahl(prev => { const s = new Set(prev); s.has(n.id) ? s.delete(n.id) : s.add(n.id); return s; });
  }
  async function pin(n: Notiz) {
    try { await setzePin(n, !n.angepinnt); signalNotizenGeaendert(); }
    catch (err) { zeigeApiFehler(err, 'Speichern fehlgeschlagen'); }
  }
  async function archivEinzeln(n: Notiz) {
    try { await setzeArchiv(n, !n.archiviert); toast(n.archiviert ? 'Notiz zurückgeholt' : 'Notiz archiviert', 'success'); signalNotizenGeaendert(); }
    catch (err) { zeigeApiFehler(err, 'Speichern fehlgeschlagen'); }
  }
  async function archivAuswahl() {
    const ziel = liste.filter(n => auswahl.has(n.id));
    try {
      for (const n of ziel) await setzeArchiv(n, !archiv);
      toast(`${ziel.length} ${ziel.length === 1 ? 'Notiz' : 'Notizen'} ${archiv ? 'zurückgeholt' : 'archiviert'}`, 'success');
      setAuswahlModus(false); setAuswahl(new Set());
      signalNotizenGeaendert();
    } catch (err) { zeigeApiFehler(err, 'Speichern fehlgeschlagen'); }
  }

  const zeile = (n: Notiz) => {
    const k = katInfo(n.kategorie); const gewaehlt = auswahl.has(n.id);
    return (
      <div key={n.id} className={`ld-nz-zeile ld-nz-zeile--k2${gewaehlt ? ' ld-nz-zeile--gewaehlt' : ''}`}>
        {auswahlModus && (
          <input type="checkbox" className="ld-nz-check" checked={gewaehlt} onChange={() => umschalten(n)} aria-label={`${n.schueler_name}, ${ttmm(n.datum)} auswählen`} />
        )}
        <button className="ld-nz-zeile__haupt" onClick={() => auswahlModus ? umschalten(n) : setSheet({ notiz: n })}
          aria-label={`Notiz zu ${n.schueler_name} vom ${ttmm(n.datum)} ${auswahlModus ? 'auswählen' : 'öffnen'}`}>
          <span className="ld-kl-avatar" aria-hidden="true">{initialen(n.schueler_name)}</span>
          <span className="ld-nz-zeile__text">
            <span className="ld-nz-k2-kopf">
              <b>{n.schueler_name}</b>
              {n.angepinnt === 1 && <MIcon name="push_pin" size={16} className="ld-nz-pinmarke" label="angepinnt" />}
              {!eingebettet && n.klasse_name && <span className="ld-nz-klasse">{n.klasse_name}</span>}
              <span className="ld-nz-k2-datum">{wtKommaTm(n.datum)}</span>
            </span>
            <span className="ld-nz-zeile__notiz">{notizText(n)}</span>
            <span className="ld-nz-k2-kat"><i style={{ background: k.punkt ?? 'var(--ld-text-tertiary)' }} aria-hidden="true" />{k.label}{n.fach ? <span className="ld-nz-k2-fach">, {n.fach}</span> : null}</span>
          </span>
        </button>
        {!auswahlModus && (
          <button className="ld-nz-icon ld-nz-k2-mehr" onClick={e => setMenu({ id: n.id, el: e.currentTarget })} aria-label="Weitere Aktionen" aria-haspopup="menu" aria-expanded={menu?.id === n.id}>
            <MIcon name="more_horiz" size={19} />
          </button>
        )}
      </div>
    );
  };
  const menuNotiz = menu ? liste.find(n => n.id === menu.id) ?? null : null;

  const filterAktiv = !!(klasseId && !eingebettet) || !!schuelerId || schuljahr !== 'aktuell' || !!sucheVerzoegert || !!kategorie;
  const kindName = schuelerId ? schuelerOptionen.find(s => s.id === schuelerId)?.name ?? 'Kind' : 'Alle Kinder';
  return (
    <div className="ld-nz ld-page-enter">
      {!eingebettet && (
        <div className="ld-nz-k2-titel">
          <h1>Notizen</h1>
          <p>Beobachtungen zu einzelnen Kindern, {sjAktuell}</p>
        </div>
      )}

      <label className="ld-kl-suche" style={{ display: 'block', marginBottom: 10 }}>
        <MIcon name="search" size={20} />
        <input type="search" value={suche} onChange={e => setSuche(e.target.value)} placeholder="In Notizen suchen" aria-label="In Notizen suchen" />
      </label>

      <div className="ld-nz-k2-leiste">
        <div className="ld-kl-umschalter" role="group" aria-label="Ansicht">
          <button type="button" aria-pressed={!archiv} onClick={() => setArchiv(false)}>Aktuell</button>
          <button type="button" aria-pressed={archiv} onClick={() => setArchiv(true)}>Archiv{anzahl.archiv > 0 ? ` ${anzahl.archiv}` : ''}</button>
        </div>
        <label className="ld-nz-k2-select">
          <LdAuswahl value={schuelerId} onChange={e => setSchuelerId(e.target.value ? Number(e.target.value) : '')} aria-label="Kind">
            <option value="">Alle Kinder</option>
            {schuelerOptionen.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </LdAuswahl>
          <span aria-hidden="true">{kindName}</span><MIcon name="expand_more" size={18} />
        </label>
        {!eingebettet && (
          <label className="ld-nz-k2-select">
            <LdAuswahl value={klasseId} onChange={e => { setKlasseId(e.target.value ? Number(e.target.value) : ''); setSchuelerId(''); }} aria-label="Klasse">
              <option value="">Alle Klassen</option>
              {klassen.map(k => <option key={k.klasse_id} value={k.klasse_id}>Klasse {k.klasse_name}</option>)}
            </LdAuswahl>
            <span aria-hidden="true">{klasseId ? `Klasse ${klassen.find(k => k.klasse_id === klasseId)?.klasse_name ?? ''}` : 'Alle Klassen'}</span><MIcon name="expand_more" size={18} />
          </label>
        )}
        <button type="button" className={`ld-nz-icon ld-nz-icon--rand${filterOffen || kategorie || schuljahr !== 'aktuell' ? ' aktiv' : ''}`} aria-expanded={filterOffen}
          onClick={() => setFilterOffen(o => !o)} aria-label="Weitere Filter" title="Weitere Filter"><MIcon name="tune" size={19} /></button>
        <button type="button" className={`ld-nz-icon ld-nz-icon--rand${auswahlModus ? ' aktiv' : ''}`} onClick={() => { setAuswahlModus(v => !v); setAuswahl(new Set()); }}
          aria-pressed={auswahlModus} aria-label="Mehrere auswählen" title="Mehrere auswählen"><MIcon name="checklist" size={19} /></button>
        {kopfZusatz}
        {!isMobile && <button type="button" className="ld-kl-haupt ld-nz-k2-neu" onClick={() => setSheet({ notiz: null })}><MIcon name="add" size={19} />Notiz</button>}
      </div>

      {filterOffen && (
        <div className="ld-nz-k2-leiste">
          <label className="ld-nz-k2-select">
            <LdAuswahl value={kategorie ?? ''} onChange={e => setKategorie(e.target.value || null)} aria-label="Kategorie">
              <option value="">Alle Kategorien</option>
              {katListe.map(k => <option key={k} value={k}>{katInfo(k).label} ({kategorien[k] ?? 0})</option>)}
            </LdAuswahl>
            <span aria-hidden="true">{kategorie ? katInfo(kategorie).label : 'Alle Kategorien'}</span><MIcon name="expand_more" size={18} />
          </label>
          <label className="ld-nz-k2-select">
            <LdAuswahl value={schuljahr} onChange={e => setSchuljahr(e.target.value)} aria-label="Schuljahr">
              {sjOptionen.map(o => <option key={o.wert} value={o.wert}>Schuljahr {o.label}</option>)}
            </LdAuswahl>
            <span aria-hidden="true">Schuljahr {sjOptionen.find(o => o.wert === schuljahr)?.label}</span><MIcon name="expand_more" size={18} />
          </label>
        </div>
      )}

      {isMobile && <button type="button" className="ld-kl-haupt" style={{ width: '100%', margin: '4px 0 6px' }} onClick={() => setSheet({ notiz: null })}><MIcon name="add" size={19} />Notiz</button>}

      {archiv && <div className="ld-nz-hinweis"><MIcon name="info" size={16} />Archivierte Notizen bleiben in deinem Datenexport.</div>}

      {notizen === null ? (
        <div className="ld-kl-karte ld-nz-leer">Lade Notizen …</div>
      ) : liste.length === 0 ? (
        <div className="ld-kl-karte ld-nz-leer">
          {filterAktiv ? 'Keine Notizen zu diesen Filtern.' : archiv ? 'Im Archiv liegen keine Notizen.' : 'Noch keine Notizen. Lege mit „+ Notiz“ die erste an.'}
        </div>
      ) : (
        <>
          {gruppen.map(([titel, rows]) => (
            <React.Fragment key={titel}>
              <h2 className="ld-kl-abschnitt">{titel === 'Angepinnt' && <MIcon name="push_pin" size={16} />}{titel} <span className="ld-kl-abschnitt__zahl">{rows.length}</span></h2>
              <div className="ld-kl-karte ld-nz-k2-karte">{rows.map(zeile)}</div>
            </React.Fragment>
          ))}
          {liste.length < gesamt && (
            <div className="ld-nz-mehr">
              <button onClick={() => lade(true, liste)}><MIcon name="expand_more" size={18} />Weitere {gesamt - liste.length} Notizen laden</button>
            </div>
          )}
        </>
      )}
      {auswahlModus && (
        <div className="ld-nz-auswahlleiste" role="toolbar" aria-label="Auswahl">
          <span><MIcon name="checklist" size={18} />{auswahl.size} ausgewählt</span>
          <button disabled={auswahl.size === 0} onClick={() => kopiereNotizen(liste.filter(n => auswahl.has(n.id)))}><MIcon name="content_copy" size={17} />Als Text kopieren</button>
          <button disabled={auswahl.size === 0} onClick={archivAuswahl}><MIcon name={archiv ? 'unarchive' : 'archive'} size={17} />{archiv ? 'Zurückholen' : 'Archivieren'}</button>
          <button onClick={() => { setAuswahlModus(false); setAuswahl(new Set()); }}>Abbrechen</button>
        </div>
      )}

      {menu && menuNotiz && (
        <PortalMenu anker={menu.el} onClose={() => setMenu(null)} label="Weitere Aktionen">
          <button type="button" role="menuitem" className="ld-kk-menu__item" onClick={() => { setMenu(null); setSheet({ notiz: menuNotiz }); }}><MIcon name="edit" size={17} />Bearbeiten</button>
          <button type="button" role="menuitem" className="ld-kk-menu__item" onClick={() => { setMenu(null); void pin(menuNotiz); }}><MIcon name="push_pin" size={17} />{menuNotiz.angepinnt ? 'Nicht mehr anpinnen' : 'Anpinnen'}</button>
          <button type="button" role="menuitem" className="ld-kk-menu__item" onClick={() => { setMenu(null); kopiereNotizen([menuNotiz]); }}><MIcon name="content_copy" size={17} />Kopieren</button>
          <button type="button" role="menuitem" className="ld-kk-menu__item" onClick={() => { setMenu(null); void archivEinzeln(menuNotiz); }}><MIcon name={menuNotiz.archiviert ? 'unarchive' : 'archive'} size={17} />{menuNotiz.archiviert ? 'Zurückholen' : 'Archivieren'}</button>
          <button type="button" role="menuitem" className="ld-kk-menu__item" style={{ color: 'var(--ld-danger-text)' }} onClick={() => { setMenu(null); loescheMitRueckgaengig(menuNotiz); }}><MIcon name="delete" size={17} />Löschen</button>
        </PortalMenu>
      )}

      {sheet && (
        <NotizSheet notiz={sheet.notiz} vorbelegtSchuelerId={schuelerId || null} onClose={() => setSheet(null)} />
      )}
    </div>
  );
}
